import AdminLayout from "@/components/layout-admin";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Radio, Pencil, Trash2, RefreshCw, ExternalLink, Globe, Loader2 } from "lucide-react";
import { SiAirbnb } from "react-icons/si";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import type { ChannelConnection } from "@shared/schema";
import { useApartments } from "@/hooks/use-resources";


const PROVIDERS = [
  { value: "airbnb", label: "Airbnb" },
  { value: "booking_com", label: "Booking.com" },
  { value: "vrbo", label: "VRBO" },
  { value: "google_vacation", label: "Google Vacation" },
  { value: "other", label: "Other" },
] as const;

function getProviderLabel(provider: string) {
  return PROVIDERS.find((p) => p.value === provider)?.label || provider;
}

function ProviderBadge({ provider }: { provider: string }) {
  const icon =
    provider === "airbnb" ? (
      <SiAirbnb className="w-3 h-3" />
    ) : provider === "booking_com" ? (
      <Globe className="w-3 h-3" />
    ) : (
      <Radio className="w-3 h-3" />
    );

  return (
    <Badge variant="secondary" className="gap-1">
      {icon}
      {getProviderLabel(provider)}
    </Badge>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant =
    status === "active"
      ? "default"
      : status === "paused"
        ? "secondary"
        : "destructive";
  return (
    <Badge variant={variant} data-testid="badge-status">
      {status}
    </Badge>
  );
}

function SyncStatusDot({ status }: { status: string | null }) {
  const color = status === 'success' ? 'bg-green-500' : status === 'error' ? 'bg-red-500' : 'bg-muted-foreground/40';
  return <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${color}`} />;
}

function timeAgo(date: Date | string | null): string {
  if (!date) return 'Never synced';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  return `${diffDays}d ago`;
}

interface ChannelForm {
  apartmentId: string;
  provider: string;
  icalImportUrl: string;
  icalExportUrl: string;
  externalListingId: string;
  syncEnabled: boolean;
}

const emptyForm: ChannelForm = {
  apartmentId: "",
  provider: "",
  icalImportUrl: "",
  icalExportUrl: "",
  externalListingId: "",
  syncEnabled: true,
};

export default function AdminChannels() {
  const { data: channels, isLoading } = useQuery<ChannelConnection[]>({
    queryKey: ["/api/admin/channels"],
  });
  const { data: apartments } = useApartments();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [editChannel, setEditChannel] = useState<ChannelConnection | null>(null);
  const [form, setForm] = useState<ChannelForm>(emptyForm);

  const createMutation = useMutation({
    mutationFn: async (data: ChannelForm) => {
      await apiRequest("POST", "/api/admin/channels", {
        apartmentId: Number(data.apartmentId),
        provider: data.provider,
        icalImportUrl: data.icalImportUrl || null,
        icalExportUrl: data.icalExportUrl || null,
        externalListingId: data.externalListingId || null,
        syncEnabled: data.syncEnabled,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/channels"] });
      setCreateOpen(false);
      setForm(emptyForm);
      toast({ title: "Channel connection added" });
    },
    onError: () => {
      toast({ title: "Failed to add channel connection", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ChannelForm }) => {
      await apiRequest("PUT", `/api/admin/channels/${id}`, {
        apartmentId: Number(data.apartmentId),
        provider: data.provider,
        icalImportUrl: data.icalImportUrl || null,
        icalExportUrl: data.icalExportUrl || null,
        externalListingId: data.externalListingId || null,
        syncEnabled: data.syncEnabled,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/channels"] });
      setEditChannel(null);
      setForm(emptyForm);
      toast({ title: "Channel connection updated" });
    },
    onError: () => {
      toast({ title: "Failed to update channel connection", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/admin/channels/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/channels"] });
      toast({ title: "Channel connection deleted" });
    },
    onError: () => {
      toast({ title: "Failed to delete channel connection", variant: "destructive" });
    },
  });

  const [syncingChannelId, setSyncingChannelId] = useState<number | null>(null);

  const syncMutation = useMutation({
    mutationFn: async (id: number) => {
      setSyncingChannelId(id);
      const resp = await apiRequest("POST", `/api/admin/channels/${id}/sync`);
      return resp.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/channels"] });
      setSyncingChannelId(null);
      if (data.lastSyncStatus === 'error') {
        toast({ title: "Sync completed with errors", description: data.syncError, variant: "destructive" });
      } else {
        toast({ title: "Sync completed", description: `${data.lastSyncReservationCount || 0} reservations synced` });
      }
    },
    onError: () => {
      setSyncingChannelId(null);
      toast({ title: "Sync failed", variant: "destructive" });
    },
  });

  const handleCreate = () => {
    createMutation.mutate(form);
  };

  const handleEdit = (ch: ChannelConnection) => {
    setEditChannel(ch);
    setForm({
      apartmentId: String(ch.apartmentId),
      provider: ch.provider,
      icalImportUrl: ch.icalImportUrl || "",
      icalExportUrl: ch.icalExportUrl || "",
      externalListingId: ch.externalListingId || "",
      syncEnabled: ch.syncEnabled,
    });
  };

  const handleUpdate = () => {
    if (!editChannel) return;
    updateMutation.mutate({ id: editChannel.id, data: form });
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const getApartmentName = (apartmentId: number) => {
    return apartments?.find((a) => a.id === apartmentId)?.name || `Apt #${apartmentId}`;
  };

  const formFields = (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Apartment</label>
        <Select
          value={form.apartmentId}
          onValueChange={(val) => setForm({ ...form, apartmentId: val })}
        >
          <SelectTrigger data-testid="select-apartment">
            <SelectValue placeholder="Select apartment" />
          </SelectTrigger>
          <SelectContent>
            {apartments?.map((apt) => (
              <SelectItem key={apt.id} value={String(apt.id)} data-testid={`option-apartment-${apt.id}`}>
                {apt.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">Provider</label>
        <Select
          value={form.provider}
          onValueChange={(val) => setForm({ ...form, provider: val })}
        >
          <SelectTrigger data-testid="select-provider">
            <SelectValue placeholder="Select provider" />
          </SelectTrigger>
          <SelectContent>
            {PROVIDERS.map((p) => (
              <SelectItem key={p.value} value={p.value} data-testid={`option-provider-${p.value}`}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">iCal Import URL</label>
        <Input
          placeholder="https://..."
          value={form.icalImportUrl}
          onChange={(e) => setForm({ ...form, icalImportUrl: e.target.value })}
          data-testid="input-ical-import-url"
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">iCal Export URL</label>
        <Input
          placeholder="https://..."
          value={form.icalExportUrl}
          onChange={(e) => setForm({ ...form, icalExportUrl: e.target.value })}
          data-testid="input-ical-export-url"
        />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium">External Listing ID</label>
        <Input
          placeholder="e.g. 12345678"
          value={form.externalListingId}
          onChange={(e) => setForm({ ...form, externalListingId: e.target.value })}
          data-testid="input-external-listing-id"
        />
      </div>
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-medium">Sync Enabled</label>
        <Switch
          checked={form.syncEnabled}
          onCheckedChange={(checked) => setForm({ ...form, syncEnabled: checked })}
          data-testid="switch-sync-enabled"
        />
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">
            Channel Manager
          </h1>
          <p className="text-muted-foreground mt-2">
            Manage external booking channel connections for your apartments.
          </p>
        </div>

        <Dialog
          open={createOpen}
          onOpenChange={(open) => {
            setCreateOpen(open);
            if (!open) setForm(emptyForm);
          }}
        >
          <DialogTrigger asChild>
            <Button data-testid="button-add-channel">
              <Plus className="w-4 h-4 mr-2" /> Add Connection
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Channel Connection</DialogTitle>
            </DialogHeader>
            {formFields}
            <Button
              className="w-full"
              onClick={handleCreate}
              disabled={createMutation.isPending || !form.apartmentId || !form.provider}
              data-testid="button-submit-channel"
            >
              {createMutation.isPending ? "Creating..." : "Create Connection"}
            </Button>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading && (
        <div className="text-center py-12 text-muted-foreground">Loading channels...</div>
      )}

      {!isLoading && (!channels || channels.length === 0) && (
        <div className="text-center py-12" data-testid="text-empty">
          <Radio className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">
            No channel connections yet. Add your first connection to sync with external platforms.
          </p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {channels?.map((ch) => (
          <Card key={ch.id} data-testid={`card-channel-${ch.id}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
                  <Radio className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    className="text-lg font-bold font-display truncate"
                    data-testid={`text-channel-apartment-${ch.id}`}
                  >
                    {getApartmentName(ch.apartmentId)}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <ProviderBadge provider={ch.provider} />
                    <StatusBadge status={ch.status} />
                  </div>
                  <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1 flex-wrap" data-testid={`text-sync-status-${ch.id}`}>
                      <SyncStatusDot status={ch.lastSyncStatus} />
                      <span>
                        {ch.lastSyncAt ? `Last synced: ${timeAgo(ch.lastSyncAt)}` : 'Never synced'}
                      </span>
                      {ch.lastSyncStatus && (
                        <Badge variant={ch.lastSyncStatus === "success" ? "default" : "destructive"} className="text-xs">
                          {ch.lastSyncStatus}
                        </Badge>
                      )}
                    </div>
                    {ch.lastSyncReservationCount != null && ch.lastSyncStatus === 'success' && (
                      <div className="flex items-center gap-1 flex-wrap" data-testid={`text-sync-count-${ch.id}`}>
                        <span>{ch.lastSyncReservationCount} reservation{ch.lastSyncReservationCount !== 1 ? 's' : ''} synced</span>
                      </div>
                    )}
                    {ch.syncError && ch.lastSyncStatus === 'error' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="text-xs text-destructive truncate cursor-help" data-testid={`text-sync-error-${ch.id}`}>
                            {ch.syncError}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs">
                          <p>{ch.syncError}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <div className="flex items-center gap-1 flex-wrap">
                      <RefreshCw className="w-3 h-3 flex-shrink-0" />
                      <span>
                        Sync: {ch.syncEnabled ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                    {ch.externalListingId && (
                      <div className="flex items-center gap-1 flex-wrap" data-testid={`text-listing-id-${ch.id}`}>
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">ID: {ch.externalListingId}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 mt-4 pt-4 border-t justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => syncMutation.mutate(ch.id)}
                  disabled={syncingChannelId === ch.id}
                  data-testid={`button-sync-channel-${ch.id}`}
                >
                  {syncingChannelId === ch.id ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1" />
                  )}
                  {syncingChannelId === ch.id ? "Syncing..." : "Sync Now"}
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleEdit(ch)}
                  data-testid={`button-edit-channel-${ch.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="icon"
                      variant="ghost"
                      data-testid={`button-delete-channel-${ch.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Channel Connection</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete the {getProviderLabel(ch.provider)} connection
                        for "{getApartmentName(ch.apartmentId)}"? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(ch.id)}
                        data-testid="button-confirm-delete"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog
        open={!!editChannel}
        onOpenChange={(open) => {
          if (!open) {
            setEditChannel(null);
            setForm(emptyForm);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Channel Connection</DialogTitle>
          </DialogHeader>
          {formFields}
          <Button
            className="w-full"
            onClick={handleUpdate}
            disabled={updateMutation.isPending || !form.apartmentId || !form.provider}
            data-testid="button-update-channel"
          >
            {updateMutation.isPending ? "Updating..." : "Update Connection"}
          </Button>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
